/**
 * useFreshness — v.3 wiring port of `frontend-v2/src/hooks/useFreshness.ts`
 * (v.1-D §7.11, PRD FR-007 / FR-016).
 *
 * Subscribes to `api.queries.cronHealth.getLastRunForTask` (the point-lookup
 * last-run query — NEVER `getCronHealth`, the 7-day failure-count aggregator
 * — see `specs/exploration/freshness_facts.md` §1) for a named cron task and
 * returns `{ ageMs, isStale, lastRunAt }`. Staleness is computed against the
 * per-task threshold catalogue from v.1-D §6.5 — 11 cron tasks total.
 *
 * Loading semantics: while `useQuery` returns `undefined`, the hook returns
 *   { ageMs: undefined, isStale: false, lastRunAt: undefined }
 * — `isStale: false` is deliberate: a loading state must not produce a false
 * stale-data alarm on first paint. The same applies when the backend returns
 * `null` (cron never observed — freshness_facts.md §4 documents
 * `slo-daily-rollup` is currently in this shape until a writer lands).
 *
 * v.3 port-site fix (runtime belt to the build-time braces):
 *   The v.1-D reference silently defaulted an unknown key to `Infinity`
 *   threshold — never stale — which is exactly the silent-defect pattern
 *   FR-007's "a banner bound to a non-existent key fails the build rather
 *   than rendering nothing" calls out. The build-time guardrail at
 *   `tests/guardrails/freshness.cronkeys.test.ts` is the primary defence
 *   (compares `CRON_THRESHOLDS_MS` keys against the generated cron-key
 *   snapshot). The runtime THROW here is belt-and-braces: a key that slips
 *   past CI cannot quietly render a never-stale banner — the page crashes
 *   loudly into the error-boundary instead, naming the offending key.
 *
 * Ported by T308 from `frontend-v2/src/hooks/useFreshness.ts`.
 */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';

export interface FreshnessResult {
  ageMs: number | undefined;
  isStale: boolean;
  lastRunAt: number | undefined;
}

/**
 * Per-cron-task freshness thresholds, in milliseconds.
 *
 * Source: v.1-D §6.5 (verbatim from `frontend-v2/src/hooks/useFreshness.ts`).
 * Values are recomputed inline (`10 * 60_000` etc.) rather than imported
 * from a helper so reviewers can read the catalogue directly. The eleven
 * keys are pinned by `tests/guardrails/freshness.cronkeys.test.ts` against
 * the source-generated cron-key snapshot.
 */
export const CRON_THRESHOLDS_MS = {
  'bridge-health-sweep':                10 * 60_000,
  'manifestSynthesizerHeartbeatAudit':   2 * 60 * 60_000,
  'qoeBeaconAggregator':                10 * 60_000,
  'rollup-external-api-calls':           2 * 60 * 60_000,
  'hourly-analytics-rollup':             2 * 60 * 60_000,
  'slo-daily-rollup':                   24 * 60 * 60_000 + 90 * 60_000,
  'watchdogCronHealth':                 20 * 60_000,
  'editorialPipelineRetryWatcher':      10 * 60_000,
  'cf-ip-ranges-refresh':               12 * 60 * 60_000,
  'youtube-sync':                        4 * 60_000,
  'ad-break-classifier':                10 * 60_000,
} as const satisfies Readonly<Record<string, number>>;

/**
 * Narrow union of the eleven catalogue keys. Consumed by the typed
 * `FreshnessBanner` primitive (T310) so a phantom key fails the compile,
 * and re-exported here as the runtime belt's authority on which keys are
 * legal.
 */
export type FreshnessKey = keyof typeof CRON_THRESHOLDS_MS;

export function useFreshness(cronName: string): FreshnessResult {
  // Runtime belt to the build-time braces (FR-007). A key not in
  // CRON_THRESHOLDS_MS would silently render a never-stale banner under
  // the old `?? Infinity` fallback — instead throw loudly, naming the
  // offending key so the per-card error boundary can surface it.
  if (!Object.prototype.hasOwnProperty.call(CRON_THRESHOLDS_MS, cronName)) {
    throw new Error(
      `useFreshness: unknown cron key "${cronName}". ` +
        `Valid keys are: ${Object.keys(CRON_THRESHOLDS_MS).join(', ')}.`,
    );
  }

  // Backend returns `number | null`: `null` means the cron has never been
  // observed (no rows in cronExecutionLogs). `undefined` is Convex's
  // loading state. Treat both as "not stale" so a new task or a
  // still-loading query never flashes a false alarm.
  const lastRunAt = useQuery(
    api.queries.cronHealth.getLastRunForTask,
    { taskName: cronName },
  ) as number | null | undefined;

  if (lastRunAt === undefined || lastRunAt === null) {
    return { ageMs: undefined, isStale: false, lastRunAt: undefined };
  }
  const ageMs = Date.now() - lastRunAt;
  const threshold =
    CRON_THRESHOLDS_MS[cronName as FreshnessKey];
  return { ageMs, isStale: ageMs > threshold, lastRunAt };
}
